import { useState, useEffect } from 'react';
import { authService } from '../../backend/services/auth';
import { rbacService } from '../../backend/services/rbac';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'user';
  isSuspended?: boolean;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const checkAuth = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          // Check suspension status
          let isSuspended = false;
          try {
            const userProfile = await rbacService.getUserProfile(currentUser.id);
            isSuspended = userProfile?.status === 'suspended';
          } catch (profileError) {
            console.warn('Could not check suspension status:', profileError);
            // If we can't check suspension status, allow access (fail open)
          }

          setUser({
            id: currentUser.id,
            email: currentUser.email || '',
            role: 'admin', // TODO: Get actual role from user metadata or database
            isSuspended
          });
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = authService.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Check suspension status for newly signed in user
        let isSuspended = false;
        try {
          const userProfile = await rbacService.getUserProfile(session.user.id);
          isSuspended = userProfile?.status === 'suspended';
        } catch (profileError) {
          console.warn('Could not check suspension status:', profileError);
          // If we can't check suspension status, allow access (fail open)
        }

        setUser({
          id: session.user.id,
          email: session.user.email || '',
          role: 'admin', // TODO: Get actual role
          isSuspended
        });
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const isAdmin = user?.role === 'admin';
  const isAuthenticated = !!user;

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await authService.signInWithEmail(email, password);
      if (error) {
        return { error };
      }
      // The auth state change listener will handle updating the user state
      return { error: null };
    } catch (error) {
      console.error('Sign in failed:', error);
      return { error: { message: 'An unexpected error occurred. Please try again.' } };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await authService.signUpWithEmail(email, password);
      if (error) {
        return { error };
      }
      // The auth state change listener will handle updating the user state
      return { error: null };
    } catch (error) {
      console.error('Sign up failed:', error);
      return { error: { message: 'An unexpected error occurred. Please try again.' } };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await authService.signOut();
      if (error) {
        console.error('Sign out error:', error);
      } else {
        setUser(null);
        // Redirect to login page
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  return {
    user,
    isAdmin,
    isAuthenticated,
    loading,
    signIn,
    signUp,
    signOut
  };
};